import setHours from "date-fns/setHours"
import setMinutes from "date-fns/setMinutes"
import PropTypes from "prop-types"
import React from "react"
import { Controller, useFieldArray, useFormContext } from "react-hook-form"

import Button from "src/components/Button"
import Form from "src/components/Form"

const timeToMinutes = (timeString) => {
  if (!timeString) return null
  const [hours, minutes] = timeString.split(":").map(Number)
  return hours * 60 + minutes
}

const OperatingHoursInput = ({ name, disabled = false }) => {
  const {
    control,
    getValues,
    formState: { errors },
  } = useFormContext()

  const { fields, append, remove } = useFieldArray({
    control,
    name,
  })

  const parseTimeString = (timeString) => {
    if (!timeString) return null
    const [hours, minutes] = timeString.split(":").map(Number)
    return setHours(setMinutes(new Date(), minutes), hours)
  }

  const handleAddTimeRange = () => {
    append({ startTime: "08:00", endTime: "18:00" })
  }

  const validateTimeRange = (value, index, startOrEnd) => {
    const timeRanges = getValues(name)
    if (!timeRanges || timeRanges.length === 0) return true

    const currentEntry = timeRanges[index]
    if (!currentEntry) return true

    const startTime = startOrEnd === "start" ? value : currentEntry.startTime
    const endTime = startOrEnd === "end" ? value : currentEntry.endTime

    const startMinutes = timeToMinutes(startTime)
    const endMinutes = timeToMinutes(endTime)

    if (
      startMinutes !== null &&
      endMinutes !== null &&
      startMinutes >= endMinutes
    ) {
      return "Start time must be before end time"
    }

    const hasOverlap = timeRanges.some((other, otherIndex) => {
      if (otherIndex === index) return false
      const otherStart = timeToMinutes(other.startTime)
      const otherEnd = timeToMinutes(other.endTime)
      if (
        otherStart === null ||
        otherEnd === null ||
        startMinutes === null ||
        endMinutes === null
      ) {
        return false
      }
      return startMinutes < otherEnd && otherStart < endMinutes
    })

    if (hasOverlap) {
      return "Operating hours cannot overlap"
    }

    return true
  }

  const getFieldError = (index, field) => {
    return errors?.[name]?.[index]?.[field]
  }

  return (
    <div>
      {fields.length === 0 ? (
        <div>
          <Button
            variant="ghost"
            onClick={handleAddTimeRange}
            disabled={disabled}
            type="button"
          >
            Add operating hours
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {fields.map((field, index) => (
            <div key={field.id}>
              <div className="flex items-center gap-2">
                <Controller
                  control={control}
                  name={`${name}.${index}.startTime`}
                  rules={{
                    validate: (value) =>
                      validateTimeRange(value, index, "start"),
                  }}
                  render={({ field: { onChange, value } }) => (
                    <Form.TimePicker
                      onChange={(date) => {
                        if (date) {
                          const hours = String(date.getHours()).padStart(2, "0")
                          const minutes = String(date.getMinutes()).padStart(
                            2,
                            "0"
                          )
                          onChange(`${hours}:${minutes}`)
                        }
                      }}
                      value={parseTimeString(value)}
                      id={`${name}-${index}-start`}
                      timeIntervals={15}
                      minTime={setHours(setMinutes(new Date(), 0), 5)}
                      maxTime={setHours(setMinutes(new Date(), 0), 22)}
                      disabled={disabled}
                      hasErrors={!!getFieldError(index, "startTime")}
                    />
                  )}
                />
                <span>to</span>
                <Controller
                  control={control}
                  name={`${name}.${index}.endTime`}
                  rules={{
                    validate: (value) => validateTimeRange(value, index, "end"),
                  }}
                  render={({ field: { onChange, value } }) => (
                    <Form.TimePicker
                      onChange={(date) => {
                        if (date) {
                          const hours = String(date.getHours()).padStart(2, "0")
                          const minutes = String(date.getMinutes()).padStart(
                            2,
                            "0"
                          )
                          onChange(`${hours}:${minutes}`)
                        }
                      }}
                      value={parseTimeString(value)}
                      id={`${name}-${index}-end`}
                      timeIntervals={15}
                      minTime={setHours(setMinutes(new Date(), 0), 5)}
                      maxTime={setHours(setMinutes(new Date(), 0), 22)}
                      disabled={disabled}
                      hasErrors={!!getFieldError(index, "endTime")}
                    />
                  )}
                />
                {fields.length > 1 && (
                  <Button
                    variant="ghost"
                    onClick={() => remove(index)}
                    disabled={disabled}
                    type="button"
                    title="Remove time range"
                  >
                    <i className="fa-solid fa-trash-can" />
                  </Button>
                )}
              </div>
              {getFieldError(index, "startTime") && (
                <Form.Error>
                  {getFieldError(index, "startTime").message}
                </Form.Error>
              )}
            </div>
          ))}
          <Button
            variant="ghost"
            onClick={handleAddTimeRange}
            disabled={disabled}
            type="button"
          >
            Add operating hours
          </Button>
        </div>
      )}
    </div>
  )
}

OperatingHoursInput.propTypes = {
  name: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
}

export default OperatingHoursInput
